import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Select, type ComboboxItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { IconStarFilled } from '@tabler/icons-react';
import { orderService } from '@/api/services/OrderService';
import { useOptionalAuth } from '@/stores/authContext';
import { renderBasisOption } from '@/components/app/buy/basisOption';
import { addressOptionKey } from '@/lib/addressOptions';
import { assembleBasisData, basisLabel, type BasisPickerItem, type BasisValue, type OwnBasis } from '@/lib/basisPicker';

const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

interface BasisPickerProps {
  /** Выбранный базис (контролируемое значение). */
  readonly value: BasisValue | null;
  readonly onChange: (value: BasisValue | null) => void;
  /** Свои базисы (мои прайсы/предложения). Пусто → группа «Мои базисы» не показывается. */
  readonly ownBases?: readonly OwnBasis[];
  readonly label?: ReactNode;
  readonly placeholder?: string;
  readonly description?: ReactNode;
  readonly error?: ReactNode;
  readonly miw?: number | string;
}

// Мой базис помечаем звездой; рендер строки («Название — адрес») — общий renderBasisOption.
function renderPickerOption({ option }: { option: ComboboxItem }) {
  const item = option as BasisPickerItem;
  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      {item.isOwn ? (
        <IconStarFilled size={12} color="var(--mantine-color-wheat-6)" style={{ flexShrink: 0 }} />
      ) : null}
      <span style={{ minWidth: 0 }}>{renderBasisOption({ option })}</span>
    </Group>
  );
}

/**
 * Единый выбор базиса: один комбобокс с двумя группами — «Мои базисы» (свои, локальный фильтр) и
 * «Справочник» (удалённый поиск по `/order/address/search`, ≥3 символа). Переиспользует
 * orderService.searchAddress + assembleBasisData (дедуп/группировка). Отображение выбранного не
 * зависит от наличия опции в текущем `data`: Mantine держит label в контролируемом searchValue.
 * Док: docs/pages/trade/basis-picker-ux.md, docs/pages/trade/basis-search-algorithm.md.
 */
export default function BasisPicker({ value, onChange, ownBases = [], label, placeholder, description, error, miw }: BasisPickerProps) {
  const { t } = useTranslation();
  const isAuthenticated = useOptionalAuth()?.isAuthenticated ?? false;

  const selectedLabel = value ? basisLabel(value) : null;
  const [search, setSearch] = useState(() => selectedLabel ?? '');
  const [debounced] = useDebouncedValue(search, DEBOUNCE_MS);

  // Когда ввод совпадает с label выбранного — это «нет активного поиска»: справочник повторно не
  // дёргаем и снова показываем все свои базисы (а не отфильтрованные строкой выбранного).
  const activeQuery = selectedLabel && search === selectedLabel ? '' : search;
  const activeDebounced = selectedLabel && debounced === selectedLabel ? '' : debounced;

  const directory = useQuery({
    queryKey: ['addressSearch', activeDebounced] as const,
    queryFn: ({ signal }) => orderService.searchAddress(activeDebounced, signal),
    enabled: isAuthenticated && activeDebounced.trim().length >= MIN_QUERY,
    staleTime: 60_000,
  });

  const { data, lookup } = useMemo(
    () =>
      assembleBasisData({
        ownBases,
        directory: directory.data ?? [],
        query: activeQuery,
        ownGroupLabel: t('basisPicker.ownGroup'),
        directoryGroupLabel: t('basisPicker.directoryGroup'),
      }),
    [ownBases, directory.data, activeQuery, t],
  );

  const selectValue = value ? addressOptionKey({ address: value.address, title: value.addressTitle }) : null;

  const handleChange = (next: string | null): void => {
    if (!next) {
      onChange(null);
      setSearch('');
      return;
    }
    const picked = lookup.get(next);
    if (picked) {
      onChange(picked);
      setSearch(basisLabel(picked));
    }
  };

  const nothingFound = directory.isFetching
    ? t('basisPicker.searching')
    : activeQuery.trim().length < MIN_QUERY
      ? t('basisPicker.typeMore', { count: MIN_QUERY })
      : t('basisPicker.nothingFound');

  return (
    <Select
      label={label}
      placeholder={placeholder}
      description={description}
      error={error}
      data={data}
      value={selectValue}
      onChange={handleChange}
      searchValue={search}
      onSearchChange={setSearch}
      renderOption={renderPickerOption}
      filter={({ options }) => options}
      nothingFoundMessage={nothingFound}
      searchable
      clearable
      openOnFocus
      miw={miw}
    />
  );
}
